import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Ruler } from 'lucide-react';
import i18next from 'internationalization';
import { CloudinaryPicture } from 'components/rich';
import useDataContext from 'data/context/data';
import productReducer from 'data/lib/productReducer';
import { formatPrice } from 'utils/format';
import {
  computePriceData,
  getInitialConfiguration,
  getSecondaryProductOptions,
} from 'ecommerce/lib/productConfiguration';

import styles from './product.module.scss';

// Same option order as the legacy picker (ProductConfiguration)
const sortValues = (values, sort) => {
  if (sort === 'asc') return [...values].sort((a, b) => (a < b ? -1 : 1));
  if (sort === 'desc') return [...values].sort((a, b) => (a > b ? -1 : 1));
  return values;
};

// Short values (generations 3 to 9) render as a compact chip row.
const isChipGroup = (values) => values.every((value) => String(value).length <= 2);

// Rows of at most three buttons, four options form a 2 by 2 grid.
const columnsFor = (count) => (count <= 3 ? count : 2);

// Small photo of the option, e.g. the cover material
const Swatch = ({ imageKey }) => (
  <span className={styles.swatch} aria-hidden="true">
    <CloudinaryPicture prefix="products/" imageKey={imageKey} type="small" alt="" />
  </span>
);

Swatch.propTypes = {
  imageKey: PropTypes.string.isRequired,
};

const OptionDetails = ({ details }) => (
  <div className={styles.optionDetails}>
    <ul>
      {details.bullets.map((bullet) => (
        <li key={bullet}>{bullet}</li>
      ))}
    </ul>
    {details.image && <img src={details.image} alt="" className={styles.optionDiagram} />}
  </div>
);

OptionDetails.propTypes = {
  details: PropTypes.shape({
    bullets: PropTypes.arrayOf(PropTypes.string),
    image: PropTypes.string,
  }).isRequired,
};

/**
 * Picker of the redesigned PDP: one button group per reducer key, info-only keys as a
 * single line, then the bundle choice. Selection and price come from the same helpers as
 * the legacy ProductConfiguration, only the markup differs. Supports reducer products with
 * bundles. Upsells and bundle options are not supported yet (docs/mycanvas-product-page.md).
 * Render it only for products with a reducer: on mount it reports its price data, which is
 * empty without one.
 */
const ProductPicker = ({ product, skuProduct, defaultPrintOnly, hidePrintOnly, onChange }) => {
  const dataContext = useDataContext();
  const picker = product.config.picker ?? {};

  const [configuration, setConfiguration] = useState(() =>
    getInitialConfiguration(product, [], defaultPrintOnly ? -1 : 0),
  );

  // dataContext's identity changes on every provider render, its SKU data is fixed after mount
  const bundleOptions = useMemo(
    () => getSecondaryProductOptions(product, skuProduct, dataContext),
    [product, skuProduct],
  );

  const priceData = useMemo(
    () => computePriceData(product, [], bundleOptions, [], configuration),
    [product, bundleOptions, configuration],
  );

  useEffect(() => {
    onChange(priceData);
  }, [priceData]);

  const select = (key, value) => {
    const next = { ...configuration, [key]: value };
    if (productReducer(product.reducer, product.options, next)) {
      setConfiguration(next);
      return;
    }

    // The combination has no SKU, keep the new value and move the others to the closest SKU
    const available = productReducer(product.reducer, product.options, {
      ...next,
      firstAvailable: value,
    });
    if (available) {
      setConfiguration({ ...available, secondaryIndex: configuration.secondaryIndex });
    }
  };

  const groups = productReducer(product.reducer, product.options);
  const selected = {};

  const renderGroup = ({ key, label, sort, values, infoOnly, availableOnly }) => {
    const groupLabel = picker.labels?.[key] ?? label;
    const value = configuration[key];

    if (infoOnly || values.length === 1) {
      const note = picker.infoNotes?.[key];
      return (
        <div key={key} className={styles.pickerInfo}>
          <Ruler size={20} aria-hidden="true" />
          <span className={styles.pickerLabel}>{groupLabel}</span>
          <span className={styles.pickerValue}>{note ? `${value} ${note}` : value}</span>
        </div>
      );
    }

    // Earlier groups narrow this one, like the legacy picker
    const constraints = { ...selected };
    const isDisabled = (option) =>
      availableOnly &&
      !productReducer(product.reducer, product.options, {
        ...constraints,
        [key]: option,
        isPossible: true,
      });

    const options = sortValues(values, sort);
    const chips = isChipGroup(options);
    // Options with a swatch need the full width on phones, they stack into a list there
    const hasSwatches = options.some((option) => picker.swatches?.[`${key}.${option}`]);
    const gridClassName = classNames(styles.optionGrid, { [styles.optionList]: hasSwatches });
    const labelId = `picker-${key}`;
    const details = picker.details?.[`${key}.${value}`];

    return (
      <div key={key} className={styles.pickerGroup}>
        <div className={styles.pickerLabelRow} id={labelId}>
          <span className={styles.pickerLabel}>{groupLabel}</span>
          <span className={styles.pickerValue}>{value}</span>
        </div>
        <div
          role="group"
          aria-labelledby={labelId}
          className={chips ? styles.chipRow : gridClassName}
          style={chips ? undefined : { '--columns': columnsFor(options.length) }}
        >
          {options.map((option) => {
            const swatch = picker.swatches?.[`${key}.${option}`];
            return (
              <button
                key={option}
                type="button"
                className={classNames(chips ? styles.chip : styles.option, {
                  [styles.selected]: option === value,
                })}
                aria-pressed={option === value}
                disabled={isDisabled(option)}
                onClick={() => select(key, option)}
              >
                {swatch && <Swatch imageKey={swatch.imageKey} />}
                {option}
              </button>
            );
          })}
        </div>
        {details && <OptionDetails details={details} />}
      </div>
    );
  };

  const groupNodes = groups.map((group) => {
    const node = renderGroup(group);
    selected[group.key] = configuration[group.key];
    return node;
  });

  const renderBundles = () => {
    const printOnlyLabel = i18next.t(
      `printsonly.${skuProduct.id}`,
      picker.printOnlyLabel ?? 'Prints Only',
    );
    const printOnlySelected =
      configuration.secondaryIndex === -1 || configuration.secondaryIndex >= bundleOptions.length;
    const priceDiff = (price) => {
      const diff = price - priceData.price;
      if (diff === 0) return null;
      // Real minus sign, formatPrice has none for negative amounts
      return `${diff > 0 ? '+' : '−'}${formatPrice(Math.abs(diff), skuProduct.currency)}`;
    };

    const printOnly = (
      <button
        key="print-only"
        type="button"
        className={classNames(styles.bundle, { [styles.selected]: printOnlySelected })}
        aria-pressed={printOnlySelected}
        onClick={() => select('secondaryIndex', -1)}
      >
        <span className={styles.bundleLabel}>{printOnlyLabel}</span>
        {priceDiff(skuProduct.price) && (
          <span className={styles.bundleDiff}>{priceDiff(skuProduct.price)}</span>
        )}
      </button>
    );

    const bundles = bundleOptions.map((bundle, index) => {
      // Lowest separate price of the bundle's secondary items, as in the legacy picker
      const savings =
        Math.min(
          ...bundle.products.secondary
            .map((id) => dataContext.getPrice(id))
            .filter(Boolean)
            .map(({ price }) => price),
        ) +
        skuProduct.price -
        bundle.price;
      const isSelected = configuration.secondaryIndex === index;

      return (
        <button
          key={bundle.id}
          type="button"
          className={classNames(styles.bundle, { [styles.selected]: isSelected })}
          aria-pressed={isSelected}
          onClick={() => select('secondaryIndex', index)}
        >
          <span className={styles.bundleLabel}>{bundle.secondaryCaption}</span>
          {priceDiff(bundle.price) && (
            <span className={styles.bundleDiff}>{priceDiff(bundle.price)}</span>
          )}
          <span className={styles.bundleBadge}>
            {savings > 0
              ? `Recommended. Save ${formatPrice(savings, bundle.currency)}`
              : 'Recommended'}
          </span>
        </button>
      );
    });

    return (
      <div className={styles.pickerGroup}>
        <div className={styles.pickerLabelRow} id="picker-bundle">
          <span className={styles.pickerLabel}>Option</span>
        </div>
        <div role="group" aria-labelledby="picker-bundle" className={styles.bundleRow}>
          {defaultPrintOnly && !hidePrintOnly && printOnly}
          {bundles}
          {!defaultPrintOnly && !hidePrintOnly && printOnly}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.picker}>
      {groupNodes}
      {skuProduct && bundleOptions.length > 0 && renderBundles()}
    </div>
  );
};

ProductPicker.propTypes = {
  product: PropTypes.object.isRequired,
  skuProduct: PropTypes.object,
  defaultPrintOnly: PropTypes.bool,
  hidePrintOnly: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

ProductPicker.defaultProps = {
  defaultPrintOnly: false,
  hidePrintOnly: false,
};

export default ProductPicker;
